/**
 * CompetitionBreakdown - League vs Cup stats
 * CupProgressWidget - Cup competition progress tracker
 * FormationAnalysis - Formation performance breakdown
 * KickOffTimeBreakdown - Games played per kick-off time
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Trophy, Award, Shield, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { cupLabel } from '../../lib/cupProgressLabels';

export function CompetitionBreakdown({ stats }) {
  if (!stats.league?.played && !stats.cup?.played) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {stats.league?.played > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />League
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-around text-center">
              <div><p className="text-2xl font-bold text-gray-900">{stats.league?.played || 0}</p><p className="text-sm text-gray-500">Played</p></div>
              <div><p className="text-2xl font-bold text-emerald-600">{stats.league?.wins || 0}</p><p className="text-sm text-gray-500">Wins</p></div>
              <div><p className="text-2xl font-bold text-amber-600">{stats.league?.draws || 0}</p><p className="text-sm text-gray-500">Draws</p></div>
              <div><p className="text-2xl font-bold text-red-600">{stats.league?.losses || 0}</p><p className="text-sm text-gray-500">Losses</p></div>
            </div>
          </CardContent>
        </Card>
      )}
      {stats.cup?.played > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" strokeWidth={1.5} />Cup Competitions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-around text-center">
              <div><p className="text-2xl font-bold text-gray-900">{stats.cup?.played || 0}</p><p className="text-sm text-gray-500">Played</p></div>
              <div><p className="text-2xl font-bold text-emerald-600">{stats.cup?.wins || 0}</p><p className="text-sm text-gray-500">Wins</p></div>
              <div><p className="text-2xl font-bold text-amber-600">{stats.cup?.draws || 0}</p><p className="text-sm text-gray-500">Draws</p></div>
              <div><p className="text-2xl font-bold text-red-600">{stats.cup?.losses || 0}</p><p className="text-sm text-gray-500">Losses</p></div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function CupProgressWidget({ cupProgress }) {
  const { user } = useAuth();
  const userLabels = user?.cup_progress_labels;
  if (!cupProgress?.length) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-600" strokeWidth={1.5} />Cup Progress
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-6">
          {cupProgress.map((cup, index) => {
            const totalRounds = cup.wins + cup.losses;
            const isEliminated = cup.status === 'eliminated';
            const isWon = cup.status === 'won';
            const wrapBg = isWon ? 'bg-amber-50' : (isEliminated ? 'bg-gray-50' : 'bg-emerald-50');
            const wrapBorder = isWon ? '#fde68a' : (isEliminated ? '#e5e5e5' : '#d1fae5');
            const trophyColor = isWon ? 'text-amber-500' : (isEliminated ? 'text-gray-400' : 'text-emerald-600');
            const badgeClass = isWon
              ? 'bg-amber-100 text-amber-700'
              : (isEliminated ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600');
            const badgeText = isWon
              ? cupLabel(userLabels, 'winner_badge', cup.latest_round)
              : (isEliminated
                ? cupLabel(userLabels, 'eliminated_badge', cup.latest_round)
                : cupLabel(userLabels, 'active_badge', cup.latest_round));
            const barFillClass = isWon ? 'bg-amber-500' : (isEliminated ? 'bg-gray-400' : 'bg-emerald-500');
            const footerText = isWon
              ? cupLabel(userLabels, 'winner_footer', cup.latest_round)
              : (isEliminated
                ? cupLabel(userLabels, 'eliminated_footer', cup.latest_round)
                : cupLabel(userLabels, 'active_footer', cup.latest_round));
            return (
              <div key={index} className={`p-3 sm:p-4 rounded-xl ${wrapBg}`}
                style={{ border: `1px solid ${wrapBorder}` }} data-testid={`cup-progress-${index}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Trophy className={`w-4 h-4 sm:w-5 sm:h-5 ${trophyColor}`} strokeWidth={1.5} />
                    <span className="text-sm sm:text-base font-medium text-gray-900">{cup.name}</span>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${badgeClass}`}>
                    {badgeText}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full ${barFillClass}`}
                      style={{ width: `${isWon ? 100 : (totalRounds > 0 ? (cup.wins / totalRounds) * 100 : 0)}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{cup.wins}W {cup.losses}L</span>
                </div>
                <p className="text-xs text-gray-500">{footerText}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export function FormationAnalysis({ formationAnalysis }) {
  if (!formationAnalysis?.length) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />Formation Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-6">
          {formationAnalysis.map((f) => (
            <div key={f.formation} className="p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-100" data-testid={`formation-stat-${f.formation}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm sm:text-base font-bold text-gray-900">{f.formation}</span>
                <Badge variant="outline" className="text-xs">{f.played} {f.played === 1 ? 'match' : 'matches'}</Badge>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-9 gap-2 text-center">
                <div><p className="text-lg sm:text-xl font-bold text-emerald-600">{f.wins}</p><p className="text-xs text-gray-500">W</p></div>
                <div><p className="text-lg sm:text-xl font-bold text-amber-600">{f.draws}</p><p className="text-xs text-gray-500">D</p></div>
                <div><p className="text-lg sm:text-xl font-bold text-red-600">{f.losses}</p><p className="text-xs text-gray-500">L</p></div>
                <div><p className="text-lg sm:text-xl font-bold text-blue-600">{f.goals_for}</p><p className="text-xs text-gray-500">GF</p></div>
                <div><p className="text-lg sm:text-xl font-bold text-gray-600">{f.goals_against}</p><p className="text-xs text-gray-500">GA</p></div>
                <div>
                  <p className={`text-lg sm:text-xl font-bold ${(f.goal_difference || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {(f.goal_difference || 0) >= 0 ? '+' : ''}{f.goal_difference || 0}
                  </p>
                  <p className="text-xs text-gray-500">GD</p>
                </div>
                <div><p className="text-lg sm:text-xl font-bold text-purple-600">{f.win_percentage || 0}%</p><p className="text-xs text-gray-500">Win%</p></div>
                <div><p className="text-lg sm:text-xl font-bold text-gray-900">{f.goals_per_game || 0}</p><p className="text-xs text-gray-500">G/G</p></div>
                <div><p className="text-lg sm:text-xl font-bold text-red-400">{f.conceded_per_game || 0}</p><p className="text-xs text-gray-500">C/G</p></div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function KickOffTimeBreakdown({ kickOffTimeBreakdown }) {
  if (!kickOffTimeBreakdown?.length) return null;
  const totalGames = kickOffTimeBreakdown.reduce((sum, row) => sum + (row.count || 0), 0);
  if (!totalGames) return null;

  return (
    <Card data-testid="kick-off-time-breakdown-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
          Kick-off Times
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-gray-500 mb-3">
          How your fixtures were scheduled across the season — includes upcoming games.
        </p>
        <div className="flex flex-col gap-2">
          {kickOffTimeBreakdown.map((row) => {
            const pct = totalGames > 0 ? Math.round((row.count / totalGames) * 100) : 0;
            return (
              <div
                key={row.time}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100"
                data-testid={`kick-off-time-row-${row.time}`}
              >
                <div className="w-24 sm:w-28 shrink-0 text-sm font-semibold text-gray-900">
                  {row.time_label}
                </div>
                <div className="flex-1 relative h-6 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-emerald-500"
                    style={{ width: `${Math.max(pct, 4)}%` }}
                  />
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  <Badge variant="outline" className="text-xs font-semibold">
                    {row.count} {row.count === 1 ? 'game' : 'games'}
                  </Badge>
                  <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

